import { DashboardCard } from "@dashboard/components/Card";
import { Multiselect } from "@dashboard/components/Combobox";
import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import {
  useWsmFitmentProductQualifierValuesSetMutation,
  WsmErrorFragment,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { buttonMessages, commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { Box, Option, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { fitmentMessages } from "./messages";
import { useFitmentExitGuard } from "./useFitmentExitGuard";
import { filterOptions, formatWsmError, idSetKey, sameIdSet } from "./utils";

export interface ProductQualifierValuesCardProps {
  productId: string;
  /** The whole vocabulary, labelled "Qualifier: value". */
  qualifierValueOptions: Option[];
  /** The ids the product carries now. */
  savedIds: string[];
  onChanged: () => Promise<unknown>;
}

/** The product's own qualifier values, apart from any application. */
export const ProductQualifierValuesCard = ({
  productId,
  qualifierValueOptions,
  savedIds,
  onChanged,
}: ProductQualifierValuesCardProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [query, setQuery] = React.useState("");
  // The selection is held as ids and labelled at render, so a relabelled
  // vocabulary never resets it.
  const [ids, setIds] = React.useState<string[]>(savedIds);
  const selected = ids.map(
    id => qualifierValueOptions.find(option => option.value === id) ?? { label: id, value: id },
  );
  // Reset to the saved set only when its content changes: a refetch after
  // another card's save returns the same ids in a new array.
  const savedKey = idSetKey(savedIds);

  React.useEffect(() => {
    setIds(savedIds);
    // savedKey stands in for savedIds, which is a new array per refetch.
  }, [savedKey]);

  const [setValues, setValuesOpts] = useWsmFitmentProductQualifierValuesSetMutation({
    disableNestedErrorHandling: true,
  });
  // Set from each call's own result, so an old failure is not shown again.
  const [errors, setErrors] = React.useState<WsmErrorFragment[]>([]);
  const dirty = !sameIdSet(ids, savedIds);

  useFitmentExitGuard(dirty);

  const save = async () => {
    setErrors([]);

    const result = await setValues({ variables: { product: productId, qualifierValues: ids } });
    const payload = result.data?.wsmFitmentProductQualifierValuesSet;

    // No payload is a transport error, which the mutation hook has toasted.
    if (!payload) {
      return;
    }

    setErrors(payload.errors);

    if (payload.errors.length === 0) {
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });

      // The write went through; a failed re-read must not look like a failed write.
      try {
        await onChanged();
      } catch {
        notify({ status: "warning", text: intl.formatMessage(fitmentMessages.savedButNotReread) });
      }
    }
  };

  return (
    <DashboardCard data-test-id="fitment-product-qualifiers">
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage(fitmentMessages.qualifiersTitle)}
        </DashboardCard.Title>
        <DashboardCard.Toolbar>
          <ConfirmButton
            transitionState={getMutationState(
              setValuesOpts.called,
              setValuesOpts.loading,
              errors,
              setValuesOpts.error ? [setValuesOpts.error] : [],
            )}
            disabled={!dirty || setValuesOpts.loading}
            onClick={save}
            data-test-id="fitment-qualifiers-save"
          >
            {intl.formatMessage(buttonMessages.save)}
          </ConfirmButton>
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content>
        <Box display="flex" flexDirection="column" gap={4}>
          <Text size={2} color="default2">
            {intl.formatMessage(fitmentMessages.qualifiersHint)}
          </Text>
          {qualifierValueOptions.length === 0 ? (
            <Text size={3} color="default2">
              {intl.formatMessage(fitmentMessages.noQualifiers)}
            </Text>
          ) : (
            <Multiselect
              name="productQualifierValues"
              data-test-id="fitment-qualifiers-select"
              label={intl.formatMessage(fitmentMessages.qualifiersLabel)}
              options={filterOptions(qualifierValueOptions, query)}
              value={selected}
              disabled={setValuesOpts.loading}
              fetchOptions={setQuery}
              onChange={event =>
                setIds((event.target.value as unknown as Option[]).map(option => option.value))
              }
            />
          )}
          {errors.map((error, index) => (
            <Text key={index} size={2} color="critical1" data-test-id="fitment-qualifiers-error">
              {formatWsmError(error)}
            </Text>
          ))}
        </Box>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export default ProductQualifierValuesCard;
